import { useQueries } from '@tanstack/react-query';

import { UserStatus } from '../../../../types/UserStatus';
import { getManagementUsers } from '../../../common/api/clients/gateway.api';
import { queries } from '../../../common/api/queryKeys/queries';

import { MemberQuickFilter, MemberQuickFilterType } from './useMemberQuickFilter';

interface UseMemberQuickFilterTotalsParams {
    searchQuery?: string;
    companyIds?: string[];
    groupIds?: string[];
}

interface FilterConfig {
    key: MemberQuickFilterType;
    statuses: UserStatus[] | undefined;
}

interface MemberQuickFilterTotals {
    all: number;
    active: number;
    pending: number;
    inactive: number;
}

export const useMemberQuickFilterTotals = ({
    searchQuery,
    companyIds,
    groupIds,
}: UseMemberQuickFilterTotalsParams): MemberQuickFilterTotals => {
    const filterValues: FilterConfig[] = [
        { key: MemberQuickFilter.ALL, statuses: undefined },
        { key: MemberQuickFilter.ACTIVE, statuses: [UserStatus.ACTIVE] },
        { key: MemberQuickFilter.PENDING, statuses: [UserStatus.INVITED] },
        { key: MemberQuickFilter.INACTIVE, statuses: [UserStatus.INACTIVE] },
    ];

    const filterQueries = useQueries({
        queries: filterValues.map((filter) => ({
            queryKey: [
                ...queries.members.totals({
                    searchQuery,
                    companyIds,
                    groupIds,
                    filterKey: filter.key,
                }).queryKey,
            ],
            queryFn: async () => {
                const result = await getManagementUsers({
                    page: 0,
                    searchQuery,
                    companyIds,
                    groupIds,
                    statuses: filter.statuses,
                });

                return result.total;
            },
        })),
    });

    const data: MemberQuickFilterTotals = {
        all: 0,
        active: 0,
        pending: 0,
        inactive: 0,
    };

    filterValues.forEach((filter, index) => {
        const query = filterQueries[index];
        if (query.data !== undefined) {
            data[filter.key] = query.data;
        }
    });

    return data;
};
